import { useState, useCallback } from 'react'

const DEMO_PROMPT_LIMIT = 20 // Increased prompt limit for demo users
const STORAGE_KEY = 'demo_prompt_count'
const RESET_DATE_KEY = 'demo_prompt_reset_date'

export const usePromptLimit = (isPublic = false) => {
    // Initialize state from localStorage
    const [promptsUsed, setPromptsUsed] = useState(() => {
        if (!isPublic) return 0 // No limit for authenticated users

        const stored = localStorage.getItem(STORAGE_KEY)
        const resetDate = localStorage.getItem(RESET_DATE_KEY)

        // Check if we need to reset (daily reset)
        if (resetDate) {
            const lastReset = new Date(resetDate)
            const now = new Date()

            // Reset if it's a new day
            if (now.toDateString() !== lastReset.toDateString()) {
                localStorage.setItem(STORAGE_KEY, '0')
                localStorage.setItem(RESET_DATE_KEY, now.toISOString())
                return 0
            }
        } else {
            // First time user
            localStorage.setItem(STORAGE_KEY, '0')
            localStorage.setItem(RESET_DATE_KEY, new Date().toISOString())
            return 0
        }

        return stored ? parseInt(stored, 10) : 0
    })

    const promptsRemaining = isPublic ? Math.max(0, DEMO_PROMPT_LIMIT - promptsUsed) : Infinity

    const canSendPrompt = useCallback(() => {
        if (!isPublic) return true // No limit for authenticated users
        return promptsUsed < DEMO_PROMPT_LIMIT
    }, [isPublic, promptsUsed])

    const incrementPromptCount = useCallback(() => {
        if (!isPublic) return true // Always allow for authenticated users

        if (promptsUsed >= DEMO_PROMPT_LIMIT) {
            return false // Limit reached
        }

        const newCount = promptsUsed + 1
        setPromptsUsed(newCount)
        localStorage.setItem(STORAGE_KEY, newCount.toString())

        // Update reset date if this is the first prompt of the day
        if (promptsUsed === 0) {
            localStorage.setItem(RESET_DATE_KEY, new Date().toISOString())
        }

        return true
    }, [isPublic, promptsUsed])

    const resetLimit = useCallback(() => {
        setPromptsUsed(0)
        localStorage.setItem(STORAGE_KEY, '0')
        localStorage.setItem(RESET_DATE_KEY, new Date().toISOString())
    }, [])

    const getLimitMessage = useCallback(() => {
        if (!isPublic) return null

        if (promptsRemaining === 0) {
            return "You've reached your daily limit of free prompts. Please sign up for unlimited access!"
        }

        if (promptsRemaining <= 3) {
            return `Only ${promptsRemaining} free prompt${promptsRemaining === 1 ? '' : 's'} remaining today`
        }

        return null
    }, [isPublic, promptsRemaining])

    return {
        promptsUsed,
        promptsRemaining,
        canSendPrompt,
        incrementPromptCount,
        resetLimit,
        getLimitMessage,
        isLimited: isPublic,
        limit: DEMO_PROMPT_LIMIT,
    }
}
